const utils = require("./utils");
const events = require("./events");
const geometry = require("./geometry");

/**
 * @param {FeatureData} featureData
 * @memberOf woosmap.maps.data
 */
class Feature {
    constructor(featureData) {
        /**
         * @type {String}
         * @private
         */
        this.id = featureData.id.toString();

        /**
         * @type {Object}
         * @private
         */
        this.properties = featureData.properties;
        /**
         * @type {google.maps.LatLng|BMap.Point}
         * @private
         */
        this.geometry = featureData.geometry;
    }

    /**
     * Returns the feature geometry.
     * @returns {google.maps.LatLng|BMap.Point}
     */
    getGeometry() {
        return this.geometry;
    }

    /**
     * Returns the feature id.
     * @returns {String}
     */
    getId() {
        return this.id;
    }

    /**
     * Returns the feature property
     * @param {String} name
     * @returns {*}
     */
    getProperty(name) {
        return this.properties[name];
    }

    /**
     * Removes property
     * @param {String} name
     */
    removeProperty(name) {
        delete this.properties[name];
    }

    /**
     * @param {String} name
     * @param {*} newValue
     */
    setProperty(name, newValue) {
        this.properties[name] = newValue;
    }

    /**
     * Exports the feature to a GeoJSON object.
     * @param {Function} callback
     */
    toGeoJson(callback) {
        callback({
            type: "Feature",
            geometry: {
                type: "Point",
                coordinates: [this.geometry.lng, this.geometry.lat],
            },
            properties: this.properties,
        });
    }
}

/**
 * A layer for displaying geospatial data. Points can be displayed.
 * @memberOf woosmap.maps.data
 */
class Data {
    /**
     * Creates an empty collection.
     */
    constructor() {
        /**
         *
         * @type {Object.<string, woosmap.maps.data.Feature>}
         * @private
         */
        this._features = {};
        this._markers = {};
        this._featureStyle = {};
        this._listeners = {};

        /**
         * @type {Function|google.maps.Icon}
         * @private
         */
        this._style = null;
    }

    /**
     * Adds the feature passed as parameter.
     * @param {woosmap.maps.data.Feature} feature
     */
    add(feature) {
        const featureId = feature.getId();
        this._features[featureId] = feature;
        this._updateFeature(feature);
        this.trigger("addfeature", { feature: feature });
    }

    /**
     * Remove the feature passed as parameter.
     * @param {woosmap.maps.data.Feature} feature
     */
    remove(feature) {
        if (feature) {
            const featureId = feature.getId();
            utils.setMap(this._markers[featureId], null);
            delete this._featureStyle[featureId];
            delete this._markers[featureId];
            delete this._features[featureId];
            this.trigger("removefeature", { feature: feature });
        }
    }

    _addFeature(feature, idPropertyName) {
        if (feature.geometry.type !== "Point") {
            throw Error("Data only supports Point geometries.");
        }

        const coordinates = feature.geometry.coordinates;

        const featureOptions = {
            geometry: {
                lat: coordinates[1],
                lng: coordinates[0],
            },
            properties: feature.properties,
        };

        if (idPropertyName !== undefined) {
            featureOptions.id = feature.properties[idPropertyName];
        }

        const _feature = new Feature(featureOptions);

        if (!this._features.hasOwnProperty(_feature.getId())) {
            this.add(_feature);
        }
    }

    /**
     * Adds GeoJSON features to the collection. Give this method a parsed JSON.
     * The imported features are returned. Throws an exception if the GeoJSON could not be imported.
     * @param {GeoJSON} geojson
     * @param {Object} [options]
     */
    addGeoJson(geojson, options) {
        options = options || {};
        const idPropertyName = options.idPropertyName;

        if (geojson.hasOwnProperty("features")) {
            for (const featureIndex in geojson.features) {
                if (geojson.features.hasOwnProperty(featureIndex)) {
                    const feature = geojson.features[featureIndex];
                    this._addFeature(feature, idPropertyName);
                }
            }
        } else if (geojson.hasOwnProperty("geometry")) {
            this._addFeature(geojson, idPropertyName);
        }
    }

    /**
     * Exports the features in the collection to a GeoJSON object.
     * @param {Function} callback
     */
    toGeoJson(callback) {
        const features = [];
        this.forEach((feature) => {
            const geom = feature.getGeometry();
            const point = new geometry.LatLng(geom.lat, geom.lng);
            features.push({
                type: "Feature",
                properties: feature.properties,
                geometry: { type: "Point", coordinates: [point.lng, point.lat] },
            });
        });

        return {
            type: "FeatureCollection",
            features: features,
        };
    }

    /**
     * Checks whether the given feature is in the collection.
     * @param {woosmap.maps.data.Feature} feature
     * @returns {boolean}
     */
    contains(feature) {
        return this._features.hasOwnProperty(feature.getId);
    }

    /**
     * Repeatedly invokes the given function, passing a feature in the collection to the function on each invocation.
     * The order of iteration through the features is undefined.
     * @param {Function} callback
     */
    forEach(callback) {
        for (const featureId in this._features) {
            if (this._features.hasOwnProperty(featureId)) {
                callback(this._features[featureId]);
            }
        }
    }

    /**
     * Returns the feature matching the id.
     * @param {String} id
     * @returns {woosmap.maps.data.Feature|null}
     */
    getFeatureById(id) {
        return this._features[id];
    }

    /**
     * Returns the map on which the features are displayed.
     * @returns {google.maps.Map|BMap.Map}
     */
    getMap() {
        return this._map;
    }

    /**
     * Renders the features on the specified map. If map is set to null, the features will be removed from the map.
     * @param {google.maps.Map|BMap.Map} map
     */
    setMap(map) {
        if (map === null && this._map) {
            for (const featureId in this._markers) {
                if (this._markers.hasOwnProperty(featureId)) {
                    const marker = this._markers[featureId];
                    utils.setMap(marker, null);
                }
            }
        }
        this._map = map;
        this._update();
    }

    /**
     * Sets the style for all features in the collection. Styles specified on a per-feature basis via `overrideStyle()`
     * continue to apply.
     * Pass either an object with the desired style options, or a function that computes the style for each feature.
     * The function will be called every time a feature's properties are updated.
     * @param {Function|google.maps.Icon} style
     */
    setStyle(style) {
        this._style = style;
        this._update();
    }

    /**
     * Gets the style for all features in the collection.
     * @returns {*}
     */
    getStyle() {
        return this._style;
    }

    /**
     * Changes the style of a feature.
     * These changes are applied on top of the style specified by `setStyle()`.
     * Style properties set to null revert to the value specified via `setStyle()`.
     * @param {woosmap.maps.data.Feature} feature
     * @param {google.maps.Icon} style
     */
    overrideStyle(feature, style) {
        this._featureStyle[feature.getId()] = style;
        this._updateFeature(feature);
    }

    /**
     * Removes the effect of previous `overrideStyle()` calls.
     * The style of the given feature reverts to the style specified by `setStyle()`.
     * If no feature is given, all features have their style reverted.
     * @param {woosmap.maps.data.Feature} [feature]
     */
    revertStyle(feature) {
        if (feature) {
            delete this._featureStyle[feature.getId()];
            this._updateFeature(feature);
        } else {
            for (const featureId in this._featureStyle) {
                if (this._featureStyle.hasOwnProperty(featureId)) {
                    const feature = this._features[featureId];
                    delete this._featureStyle[featureId];
                    this._updateFeature(feature);
                }
            }
        }
    }

    /**
     * @param {woosmap.maps.data.Feature} feature
     * @private
     */
    _updateFeature(feature) {
        let marker = this._markers[feature.getId()];
        if (marker === undefined) {
            const geometry = feature.getGeometry();
            marker = utils.getMarker({ position: utils.getLatLng(geometry.lat, geometry.lng) });
            this._markers[feature.getId()] = marker;
            utils.setMarkerOptions(marker, { zIndex: 0 });

            const getTriggerForEvent = (event, feature) => {
                return () => {
                    this.trigger(event, { feature: feature });
                };
            };

            const supportedEvents = ["click", "mouseup", "mousedown", "mouseout", "mouseover"];
            for (const supportedEventKey in supportedEvents) {
                if (supportedEvents.hasOwnProperty(supportedEventKey)) {
                    const supportedEvent = supportedEvents[supportedEventKey];

                    events.addListener(marker, supportedEvent, getTriggerForEvent(supportedEvent, feature));
                }
            }
        }

        const style = this._getStyleForFeature(feature);

        if (style) {
            utils.setMarkerOptions(marker, style);
        }

        utils.setMap(marker, this.getMap());
    }

    /**
     * @private
     */
    _update() {
        if (this.getMap()) {
            for (const featureIndex in this._features) {
                if (this._features.hasOwnProperty(featureIndex)) {
                    const feature = this._features[featureIndex];
                    this._updateFeature(feature);
                }
            }
        }
    }

    /**
     *
     * @param {woosmap.maps.data.Feature} feature
     * @private
     */
    _getStyleForFeature(feature) {
        const featureStyle = this._featureStyle[feature.getId()];
        if (featureStyle) {
            return featureStyle;
        }
        if (this._style) {
            if (typeof this._style === "function") {
                return this._style(feature);
            }
            return this._style;
        }
        return null;
    }

    /**
     * Adds a listener for event
     * @param {*} event
     * @param {Function} callback
     */
    addListener(event, callback) {
        const listeners = this._listeners[event];
        if (listeners) {
            this._listeners[event].push(callback);
        } else {
            this._listeners[event] = [callback];
        }
    }

    /**
     * Trigger event listeners.
     * @param {String} event
     * @param {*} eventData
     */
    trigger(event, eventData) {
        const listeners = this._listeners[event];
        if (listeners) {
            for (const listenerKey in listeners) {
                if (listeners.hasOwnProperty(listenerKey)) {
                    listeners[listenerKey](eventData);
                }
            }
        }
    }
}

/**
 * Returns a new data instance.
 * @memberOf woosmap.maps.data
 * @returns {google.maps.Data|woosmap.maps.data.Data}
 */
function getData() {
    return new google.maps.Data();
}

module.exports = {
    Data,
    Feature,
    getData,
};
